/**
 * A list group is DYNAMIC when its lists change by themselves — a tag
 * (performance tags move every day), a name match or a metric threshold
 * decides membership. Only a group of hand-picked lists is fixed. A fixed
 * group can follow "my order"; a dynamic one cannot (user, 19/10/2026: "when
 * the lists are dynamic there's no my order that can handle — it should be
 * based on formula"), so the planner, the replay and the editor all read a
 * dynamic group as ranked by its formula.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { ListGroupRuleNode } from "./types";

/** True when any leaf of the rule (through and / or / not) passes `test`. */
function someLeaf(rule: unknown, test: (type: string) => boolean): boolean {
  const node = rule as ListGroupRuleNode | null | undefined;
  if (!node || typeof node !== "object") return false;
  if (node.type === "and" || node.type === "or")
    return (node.nodes ?? []).some((child) => someLeaf(child, test));
  if (node.type === "not") return someLeaf(node.node, test);
  return test(node.type);
}

export function isDynamicListGroupRule(rule: unknown): boolean {
  return someLeaf(rule, (type) => type === "tag" || type === "name" || type === "metric");
}

/** Does resolving the rule need the lists' tags? Only a tag condition reads
 *  them — hand-picked lists and name matches read the catalog row alone, so
 *  a resolver can skip the (tracker-backed) tag fetch for them (19/10/2026). */
export function listGroupRuleReadsTags(rule: unknown): boolean {
  return someLeaf(rule, (type) => type === "tag");
}

/** The order a container really walks a group in: a dynamic group is always ranked by its formula. */
export function effectiveListOrderMode(orderMode: string, rule: unknown): string {
  return isDynamicListGroupRule(rule) ? "FORMULA" : orderMode;
}
